// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";
import { enCA } from "date-fns/locale";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartTextInput, SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalTimePicker,
  ModalTimePickerSlotProps,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TradeBasicsDefaults = {
  nOperation: "0",
  nOrderType: "0",
  nPrice: "0",
  nFixPipsActual: "300",
  dRateOfChangeActual: "0.00060000",
  nLimits: "3",
  nExpiration: "0",
  nSignalLogic: "0",
  strSymbol: "Current",
  strExclusion: "00:00:00-00:00:00",
  nTimeframe: "-2",
  nOneTrade: "3",
  nMaxSlippage: "10",
  nWaitingBetweenTrades: "1",
  nLocalLimits: false,
  nComfortVisualization: true,
  nGeneralSignalVisualization: false,
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nFixPipsActual: {
    min: 5,
  },
  dRateOfChangeActual: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nExpiration: {
  },
  strSymbol: {
    maxLength: 50,
    emptyAllowed: false,
    disallowedPattern: /[<>{}[\]\\|`~@#$%^&*+=]/,
  },
  nMaxSlippage: {
    min: 1,
  },
  nWaitingBetweenTrades: {
    min: -1000000,
    max: 1000000,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function TradeBasicsRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  const getOperationTypeDisplay = () => {
    const value = config.nOperation;
    switch (value) {
      case "0":
        return "Long";
      case "1":
        return "Short";
      case "2":
        return "Long signals only drawn";
      case "3":
        return "Short signals only drawn";
      default:
        return "Not set";
    }
  };

  const getOrderTypeDisplay = () => {
    const value = config.nOrderType;
    switch (value) {
      case "0":
        return "Buy";
      case "1":
        return "Buy limit";
      case "2":
        return "Buy stop";
      case "3":
        return "Sell";
      case "4":
        return "Sell limit";
      case "5":
        return "Sell stop";
      default:
        return "Not set";
    }
  };

  const getSignalLogicDisplay = () => {
    const value = config.nSignalLogic;
    if (value === "0") return "Logical AND";
    if (value === "1") return "Logical OR";
    return "Not set";
  };

  const getOneTradeModeDisplay = () => {
    const value = config.nOneTrade;
    switch (value) {
      case "0":
        return "Off";
      case "1":
        return "On & Keep pending";
      case "2":
        return "On & Delete pending";
      case "3":
        return "On & One pending";
      default:
        return "Not set";
    }
  };

  const getTimeframeDisplay = () => {
    const value = config.nTimeframe;
    switch (value) {
      case "-2":
        return "Current";
      case "0":
        return "M1";
      case "1":
        return "M5";
      case "2":
        return "M15";
      case "3":
        return "M30";
      case "4":
        return "H1";
      case "5":
        return "H4";
      case "6":
        return "D1";
      case "7":
        return "W1";
      case "8":
        return "MN";
      default:
        return "Not set";
    }
  };

  const getPriceDisplay = () => {
    const value = config.nPrice;
    switch (value) {
      case "0":
        return "Fix pips";
      case "1":
        return "Rate of change";
      case "2":
        return "Indicator value";
      case "3":
        return "Support/resistance";
      default:
        return "Not set";
    }
  };

  const getLimitsDisplay = () => {
    const value = config.nLimits;
    switch (value) {
      case "0":
        return "Initial";
      case "1":
        return "TP moved";
      case "2":
        return "SL moved";
      case "3":
        return "SL and TP moved";
      default:
        return "Not set";
    }
  };

  return (
    <Stack spacing={0.5}>
      {professionalMode === true ? (
        <>
          {renderParam("Operation type", getOperationTypeDisplay())}

          {professionalMode === true && (config.nOperation === "2" || config.nOperation === "3")
            ? renderParam("General signal visualization", config.nGeneralSignalVisualization ? "On" : "Off")
            : null}

          {renderParam("Signal logic", getSignalLogicDisplay())}
          {renderParam("Symbol", getDisplayValue("strSymbol"))}
          {renderParam("Excluded time", getDisplayValue("strExclusion"))}
          {renderParam("Timeframe", getTimeframeDisplay())}
          {renderParam("One trade mode", getOneTradeModeDisplay())}
          {renderParam("Max slippage", getDisplayValue("nMaxSlippage"))}
          {renderParam("Waiting time between trades", getDisplayValue("nWaitingBetweenTrades"))}
          {renderParam("Locally managed take profits and stop loss", config.nLocalLimits ? "On" : "Off")}
          {renderParam("Trade comfort visualization", config.nComfortVisualization ? "On" : "Off")}
        </>
      ) : (
        <>
          {renderParam("Order type", getOrderTypeDisplay())}

          {config.nOrderType !== "0" && config.nOrderType !== "3" ? (
            <>
              {renderParam("Pending price calculation method", getPriceDisplay())}

              {config.nPrice === "0" ? renderParam("Pending price distance in fix pips", getDisplayValue("nFixPipsActual")) : null}

              {config.nPrice === "1" ? renderParam("Pending price distance in rate of change", getDisplayValue("dRateOfChangeActual")) : null}

              {renderParam("Pending limits behaviour", getLimitsDisplay())}

              {config.nExpiration === "0"
                ? renderParam("Pending expiration", "Off")
                : renderParam(
                    "Pending expiration",
                    config.nExpiration < 0 ? `${Math.abs(config.nExpiration)} seconds` : `${config.nExpiration} bars`
                  )}
            </>
          ) : null}

          {renderParam("Symbol", getDisplayValue("strSymbol"))}
          {renderParam("One trade mode", getOneTradeModeDisplay())}
          {renderParam("Waiting time between trades", getDisplayValue("nWaitingBetweenTrades"))}
          {renderParam("Trade comfort visualization", config.nComfortVisualization ? "On" : "Off")}
        </>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const TradeBasicsConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  TradeBasicsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});

    // Apply all the default values to the form
    Object.keys(TradeBasicsDefaults).forEach((key) => {
      if (key !== "nOperation" && key !== "nOrderType") {
        setFieldValue(key, TradeBasicsDefaults[key]);
      }
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Helper functions for exclusion time handling
  // ----------------------------------------------------------------------
  const parseExclusionString = (exclusionString) => {
    if (!exclusionString || exclusionString === "00:00:00-00:00:00") {
      return {
        start: dayjs("2000-01-01T00:00:00"),
        end: dayjs("2000-01-01T00:00:00"),
      };
    }

    const parts = exclusionString.split("-");
    if (parts.length === 2) {
      const [startTime, endTime] = parts;
      return {
        start: dayjs(`2000-01-01T${startTime}`),
        end: dayjs(`2000-01-01T${endTime}`),
      };
    }

    return {
      start: dayjs("2000-01-01T00:00:00"),
      end: dayjs("2000-01-01T00:00:00"),
    };
  };

  const formatExclusionString = (startTime, endTime) => {
    const start = startTime ? startTime.format("HH:mm:ss") : "00:00:00";
    const end = endTime ? endTime.format("HH:mm:ss") : "00:00:00";
    return `${start}-${end}`;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      //  parameters from the original file
      nOperation: initialData?.nOperation ?? TradeBasicsDefaults.nOperation,
      nOrderType: initialData?.nOrderType ?? TradeBasicsDefaults.nOrderType,
      nPrice: initialData?.nPrice ?? TradeBasicsDefaults.nPrice,
      nFixPipsActual: initialData?.nFixPipsActual ?? TradeBasicsDefaults.nFixPipsActual,
      dRateOfChangeActual: initialData?.dRateOfChangeActual ?? TradeBasicsDefaults.dRateOfChangeActual,
      nLimits: initialData?.nLimits ?? TradeBasicsDefaults.nLimits,
      nExpiration: initialData?.nExpiration ?? TradeBasicsDefaults.nExpiration,
      nSignalLogic: initialData?.nSignalLogic ?? TradeBasicsDefaults.nSignalLogic,
      strSymbol: initialData?.strSymbol ?? TradeBasicsDefaults.strSymbol,
      strExclusion: initialData?.strExclusion ?? TradeBasicsDefaults.strExclusion,
      nTimeframe: initialData?.nTimeframe ?? TradeBasicsDefaults.nTimeframe,
      nOneTrade: initialData?.nOneTrade ?? TradeBasicsDefaults.nOneTrade,
      nMaxSlippage: initialData?.nMaxSlippage ?? TradeBasicsDefaults.nMaxSlippage,
      nWaitingBetweenTrades: initialData?.nWaitingBetweenTrades ?? TradeBasicsDefaults.nWaitingBetweenTrades,
      nLocalLimits: initialData?.nLocalLimits ?? TradeBasicsDefaults.nLocalLimits,
      nGeneralSignalVisualization: initialData?.nGeneralSignalVisualization ?? TradeBasicsDefaults.nGeneralSignalVisualization,
      nComfortVisualization: initialData?.nComfortVisualization ?? TradeBasicsDefaults.nComfortVisualization,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } = formik;

  // Parse exclusion string for time pickers
  const exclusionTimes = parseExclusionString(values.strExclusion);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }
  
  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure trade basics
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure trade basics</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nOperation */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter value="Operation type" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nOperation" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nOperation")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nOperation && errors.nOperation)}
                        >
                          <ModalContentMenuItem value="0">Long</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Short</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Long signals only drawn</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">Short signals only drawn</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {professionalMode === true && (values.nOperation === "2" || values.nOperation === "3") ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nGeneralSignalVisualization */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter value="General signal visualization" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nGeneralSignalVisualization" touched={touched} errors={errors} />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nGeneralSignalVisualization}
                          onChange={(event) => {
                            setFieldValue("nGeneralSignalVisualization", event.target.checked);
                          }}
                        />
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nOrderType */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === false ? (
                    <Box>
                      <SmartParameter value="Order type" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nOrderType" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nOrderType")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nOrderType && errors.nOrderType)}
                        >
                          <ModalContentMenuItem value="0">Buy</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Buy limit</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Buy stop</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">Sell</ModalContentMenuItem>
                          <ModalContentMenuItem value="4">Sell limit</ModalContentMenuItem>
                          <ModalContentMenuItem value="5">Sell stop</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {professionalMode === false && values.nOrderType !== "0" && values.nOrderType !== "3" ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nPrice */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Pending price calculation method"
                          helpReference="HandleTradeBasics"
                          helpText=""
                          helpLink=""
                          fieldName="nPrice"
                          touched={touched}
                          errors={errors}
                        />
                        <FormControl fullWidth>
                          <Select {...getFieldProps("nPrice")} input={<ModalContentOutlinedInput />} error={Boolean(touched.nPrice && errors.nPrice)}>
                            <ModalContentMenuItem value="0">Fix pips</ModalContentMenuItem>
                            <ModalContentMenuItem value="1">Rate of change</ModalContentMenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nFixPipsActual */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nPrice === "0" ? (
                        <Box>
                          <SmartParameter
                            value="Pending price distance in fix pips"
                            helpReference="HandleTradeBasics"
                            helpText=""
                            helpLink=""
                            fieldName="nFixPipsActual"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput
                            textFieldName="nFixPipsActual"
                            defaultLimits={DEFAULT_LIMITS}
                            formik={formik}
                          />
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* dRateOfChangeActual */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nPrice === "1" ? (
                        <Box>
                          <SmartParameter
                            value="Pending price distance in rate of change"
                            helpReference="HandleTradeBasics"
                            helpText=""
                            helpLink=""
                            fieldName="dRateOfChangeActual"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput
                            textFieldName="dRateOfChangeActual"
                            defaultLimits={DEFAULT_LIMITS}
                            formik={formik}
                          />
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nLimits */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter value="Pending limits behaviour" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nLimits" touched={touched} errors={errors} />
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nLimits")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nLimits && errors.nLimits)}
                          >
                            <ModalContentMenuItem value="0">Initial</ModalContentMenuItem>
                            <ModalContentMenuItem value="1">TP moved</ModalContentMenuItem>
                            <ModalContentMenuItem value="2">SL moved</ModalContentMenuItem>
                            <ModalContentMenuItem value="3">SL and TP moved</ModalContentMenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nExpiration */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Pending expiration"
                          helpReference="HandleTradeBasics"
                          helpText=""
                          helpLink=""
                          fieldName="nExpiration"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput
                          textFieldName="nExpiration"
                          defaultLimits={DEFAULT_LIMITS}
                          formik={formik}
                        />
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nSignalLogic */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter value="Signal logic" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nSignalLogic" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nSignalLogic")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nSignalLogic && errors.nSignalLogic)}
                        >
                          <ModalContentMenuItem value="0">Logical AND</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Logical OR</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strSymbol */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter value="Symbol" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="strSymbol" touched={touched} errors={errors} />
                    <SmartTextInput
                      textFieldName="strSymbol"
                      defaultLimits={DEFAULT_LIMITS}
                      formik={formik}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strExclusion */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter value="Excluded time range" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="strExclusion" touched={touched} errors={errors} />
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ModalTimePicker
                            value={exclusionTimes.start}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(value, exclusionTimes.end);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                          <Typography variant="contentTiny">-</Typography>
                          <ModalTimePicker
                            value={exclusionTimes.end}
                            onChange={(value) => {
                              const newExclusionString = formatExclusionString(exclusionTimes.start, value);
                              setFieldValue("strExclusion", newExclusionString);
                            }}
                            ampm={false}
                            views={["hours", "minutes", "seconds"]}
                            slotProps={ModalTimePickerSlotProps}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nTimeframe */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter value="Timeframe" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nTimeframe" touched={touched} errors={errors} />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nTimeframe")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nTimeframe && errors.nTimeframe)}
                        >
                          <ModalContentMenuItem value="-2">Current</ModalContentMenuItem>
                          <ModalContentMenuItem value="0">M1</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">M5</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">M15</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">M30</ModalContentMenuItem>
                          <ModalContentMenuItem value="4">H1</ModalContentMenuItem>
                          <ModalContentMenuItem value="5">H4</ModalContentMenuItem>
                          <ModalContentMenuItem value="6">D1</ModalContentMenuItem>
                          <ModalContentMenuItem value="7">W1</ModalContentMenuItem>
                          <ModalContentMenuItem value="8">MN</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nOneTrade */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter value="One trade mode" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nOneTrade" touched={touched} errors={errors} />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nOneTrade")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nOneTrade && errors.nOneTrade)}
                      >
                        <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                        <ModalContentMenuItem value="1">On & Keep pending</ModalContentMenuItem>
                        <ModalContentMenuItem value="2">On & Delete pending</ModalContentMenuItem>
                        <ModalContentMenuItem value="3">On & One pending</ModalContentMenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nMaxSlippage */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box>
                      <SmartParameter
                        value="Maximum slippage until order is executed"
                        helpReference="HandleTradeBasics"
                        helpText=""
                        helpLink=""
                        fieldName="nMaxSlippage"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput
                        textFieldName="nMaxSlippage"
                        defaultLimits={DEFAULT_LIMITS}
                        formik={formik}
                      />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nWaitingBetweenTrades */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Waiting time between trades"
                      helpReference="HandleTradeBasics"
                      helpText=""
                      helpLink=""
                      fieldName="nWaitingBetweenTrades"
                      touched={touched}
                      errors={errors}
                    />
                    <SmartNumberInput
                      textFieldName="nWaitingBetweenTrades"
                      defaultLimits={DEFAULT_LIMITS}
                      formik={formik}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode === true ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nLocalLimits */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Locally managed take profits and stop loss"
                          helpReference="HandleTradeBasics"
                          helpText=""
                          helpLink=""
                          fieldName="nLocalLimits"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nLocalLimits === true}
                          onChange={(event) => {
                            setFieldValue("nLocalLimits", event.target.checked);
                          }}
                        />
                      </Box>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nComfortVisualization */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter value="Trade comfort visualization" helpReference="HandleTradeBasics" helpText="" helpLink="" fieldName="nComfortVisualization" touched={touched} errors={errors} />
                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.nComfortVisualization}
                      onChange={(event) => {
                        setFieldValue("nComfortVisualization", event.target.checked);
                      }}
                    />
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
